import ThemePlaceholder from "components/Common/ThemePlaceholder"
import { CopyButton } from "components/Common/CopyButton"
import { Badge, Card, CardBody } from "reactstrap"
import { toast } from "react-toastify"
import { formatReadableDate, formatSinceDate } from "utils/formatDate"
import profilePlaceholder from "assets/images/users/avatar-1.jpg"

const coverStyle = {
  background: "linear-gradient(135deg, #f8fbff 0%, #eef7ff 48%, #e4f0ff 100%)",
  borderBottom: "1px solid rgba(85, 110, 230, 0.12)",
}

const coverTitleStyle = {
  color: "#2f3b63",
}

const coverSubtitleStyle = {
  color: "#6c7aa5",
}

const LinkWithCopy = ({ label, value }) => (
  <span className="d-inline-flex align-items-center justify-content-end gap-1">
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-sm btn-soft-primary py-0 px-2"
      title={`View ${label}`}
    >
      <i className="bx bx-link-external me-1"></i>
      View
    </a>
    <CopyButton
      text={value}
      variant="outline-secondary"
      className="py-0 px-1"
      title={`Copy ${label}`}
      successTitle={`${label} copied`}
      toastCallback={type => {
        if (type === "success") {
          toast.success(`${label} copied to clipboard.`)
        } else {
          toast.error(`Failed to copy ${label.toLowerCase()}.`)
        }
      }}
    />
  </span>
)

const detailRows = showroom => {
  const stockUrl = showroom?.stock_url

  return [
    { label: "Short Name", value: showroom?.short_name || "-" },
    { label: "Mobile", value: showroom?.mobile || "-" },
    { label: "Email", value: showroom?.email || "-" },
    {
      label: "Stock URL",
      value: stockUrl ? (
        <LinkWithCopy label="Stock URL" value={stockUrl} />
      ) : (
        "-"
      ),
    },
    { label: "Proprietor", value: showroom?.proprietor_name || "-" },
    { label: "Address", value: showroom?.address || "-" },
  ]
}

export default function ShowroomProfileCard({
  data,
  loading = false,
  onShowroom,
}) {
  if (!loading && !data) return null

  const rows = detailRows(data)

  return (
    <Card className="border-0 shadow-sm overflow-hidden mb-4">
      <div className="px-4 pt-4 pb-5" style={coverStyle}>
        {loading ? (
          <ThemePlaceholder
            style={{
              width: 160,
              height: 28,
              borderRadius: 8,
              backgroundColor: "rgba(85,110,230,.12)",
            }}
          />
        ) : (
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <h5 className="mb-1" style={coverTitleStyle}>
                Showroom Profile
              </h5>
              <p className="mb-0" style={coverSubtitleStyle}>
                Core identity, contact, and facility overview
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {onShowroom ? <>{onShowroom}</> : null}
              <Badge
                className={`border-0 ${
                  data?.is_active ? "badge-soft-success" : "badge-soft-danger"
                }`}
              >
                {data?.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        )}
      </div>

      <CardBody className="p-4" style={{ marginTop: -42 }}>
        <div className="d-flex align-items-end gap-3 mb-4">
          <div
            className="rounded-4 overflow-hidden border border-3 border-white bg-white shadow-sm flex-shrink-0"
            style={{ width: 104, height: 104 }}
          >
            {loading ? (
              <ThemePlaceholder
                style={{ width: "100%", height: "100%", borderRadius: 0 }}
              />
            ) : (
              <img
                src={data?.logo || profilePlaceholder}
                alt="Showroom Logo"
                className="w-100 h-100"
                style={{ objectFit: "cover" }}
              />
            )}
          </div>

          <div className="min-width-0 flex-grow-1">
            {loading ? (
              <>
                <ThemePlaceholder
                  style={{ width: 220, height: 26, borderRadius: 8 }}
                />
                <ThemePlaceholder
                  className="mt-2"
                  style={{ width: 180, height: 18, borderRadius: 8 }}
                />
              </>
            ) : (
              <>
                <h4 className="mb-1">
                  {data?.name || "Showroom"}
                </h4>
                <p className="text-muted mb-0">
                  {data?.address || "No address provided"}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {loading ? (
            <>
              <ThemePlaceholder
                style={{ width: 110, height: 30, borderRadius: 999 }}
              />
              <ThemePlaceholder
                style={{ width: 120, height: 30, borderRadius: 999 }}
              />
            </>
          ) : (
            <>
              <Badge color="light" className="text-primary border px-3 py-2">
                {data?.capacity ? `${data.capacity} cars` : "No capacity set"}
              </Badge>
              <Badge color="light" className="text-success border px-3 py-2">
                {data?.has_service_center ? "Service center" : "No service center"}
              </Badge>
            </>
          )}
        </div>

        <div className="specs-list">
          {rows.map(item => (
            <div
              key={item.label}
              className="d-flex justify-content-between py-2 border-bottom gap-3"
            >
              <span className="text-muted">{item.label}:</span>
              {loading ? (
                <ThemePlaceholder
                  style={{ width: 140, height: 18, borderRadius: 4 }}
                />
              ) : (
                <span className="fw-medium text-end">{item.value}</span>
              )}
            </div>
          ))}
        </div>

        {!loading && data?.created_at ? (
          <div className="pt-3 mt-3 border-top">
            <div className="small text-muted">Member Since</div>
            <div>{formatReadableDate(data.created_at)}</div>
            <div className="small text-muted fst-italic">
              {formatSinceDate(data.created_at)}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
